import { observer }              from 'mobx-react-lite';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import View                      from '@widesk-ui/components/View';
import Tooltip                   from '@widesk-ui/components/Tooltip';
import Avatar                    from '@widesk-ui/Avatar';
import { AvatarGroup }           from '@widesk-ui/Avatar';
import { ProjectUser }           from '@/TokamapEditor/stores/EditorUserStore';

/**
 * Les personnes présentes sur le PROJET, et plus seulement sur la carte ouverte — cette barre ne
 * parle que du projet.
 *
 * Tous les avatars se ressemblent, fond coloré compris : c'est l'infobulle qui dit sur quelle carte
 * se trouve chacun, et le clic qui y emmène. Une distinction visuelle a été essayée puis retirée —
 * dans un `AvatarGroup` les avatars se chevauchent, donc tout traitement qui en rend un translucide
 * laisse voir son voisin au travers.
 *
 * Ordre inchangé : celui de la liste reçue. Trier les personnes de la carte ouverte en premier
 * faisait sauter les avatars de place dès que quelqu'un changeait de carte, pour un gain nul
 * maintenant qu'ils se ressemblent tous.
 *
 * Repli : tant que `studio-socket-io` n'émet pas `server:project-users`, `projectUsers` reste vide et
 * on réaffiche les utilisateurs de la carte, exactement comme avant. Rien à coordonner entre les deux
 * déploiements.
 */
const Users = observer(() => {
	const editorUserStore = currentEditorStore.editorUserStore;

	const users: ProjectUser[] = editorUserStore.projectUsers.length
		? [...editorUserStore.projectUsers]
		: editorUserStore.users.map(user => ({ ...user, mapId: '' }));

	return (
		<View row>
			<AvatarGroup>
				{users.map(user => <UserAvatar key={user.id} user={user} />)}
			</AvatarGroup>
		</View>
	);
});

const UserAvatar = observer((props: { user: ProjectUser; }) => {
	const { user } = props;

	const projectEditor = currentProjectEditorStore.get;
	const initials = [user.lastName, user.firstName].map(s => s.at(0)).join('');

	// La carte n'est citée que si c'en est une AUTRE que celle ouverte — sinon l'infobulle répéterait
	// à chacun ce qu'il a déjà sous les yeux. Comparaison en chaînes des deux côtés : le serveur
	// renvoie le `mapId` tel qu'il l'a reçu, donc un nombre, et `123 === '123'` est faux.
	const elsewhere = !!user.mapId && String(user.mapId) !== String(projectEditor?.currentMap.id);
	const map = elsewhere
		? projectEditor?.mapCollection.models.find(m => String(m.id) === String(user.mapId))
		: undefined;

	return (
		<View>
			<Tooltip title={`${user.firstName} ${user.lastName}${map ? ` — sur ${map.label}` : ''}`} placement="top">
				<Avatar
					// Seul cas cliquable : rejoindre quelqu'un là où il travaille.
					style={{ backgroundColor: user.color, cursor: map ? 'pointer' : 'default' }}
					onClick={map ? () => projectEditor?.setCurrentMap(map) : undefined}
				>
					{initials}
				</Avatar>
			</Tooltip>
		</View>
	);
});

export default Users;
